import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Button,
  Box,
  Alert,
  CircularProgress,
  TextField,
} from "@mui/material";

import { resetPassword, verifyToken } from "../../../apis/dealer";
import { UserAuthService } from "../../../services/UserAuthService";
import { AuthLayout } from "../../../components/AuthLayout";

const SUCCESS_MSG = "Token is valid. Welcome!";

export const DealerForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("Checking...");
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const token = searchParams.get("token");
  const dispatch = useDispatch();

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await verifyToken(String(token) || "");
        if (res?.success) {
          setStatus(SUCCESS_MSG);
          setEmail(res.email);
        } else {
          setStatus(res.message);
        }
      } catch (error) {
        setStatus("Invalid or expired token.");
      }
    };

    if (token) {
      verify();
    } else {
      setStatus("No token found in the URL.");
    }
  }, [token]);

  const handleSubmit = async () => {
    try {
      await UserAuthService.login(email || "", password || "", dispatch);
      window.location.href = "/";
    } catch (error: any) {
      console.error(error);
    }
  };

  const resetPass = async () => {
    setError("");
    if (password !== confirmPassword) {
      setError("Passwords do not match. Please try again.");
      return;
    }
    setLoading(true);
    try {
      await resetPassword({ password, email, token });
      handleSubmit();
    } catch (err) {
      console.error(err);
      setError(`Failed to reset password: ${err}`);
      setLoading(false);
    }
  };

  if (status !== SUCCESS_MSG) {
    return (
      <AuthLayout title="Reset Password" subtitle="Verifying your reset link">
        <Box sx={{ textAlign: "center", py: 4 }}>
          {status === "Checking..." ? (
            <CircularProgress />
          ) : (
            <Alert severity="error">{status}</Alert>
          )}
        </Box>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Reset Password" subtitle="Enter your new password below">
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
        <TextField
          label="New Password"
          type="password"
          fullWidth
          onChange={(e) => setPassword(e.target.value)}
        />
        <TextField
          label="Confirm Password"
          type="password"
          fullWidth
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </Box>
      <Button
        fullWidth
        variant="contained"
        color="secondary"
        disabled={loading}
        onClick={resetPass}
        sx={{
          mt: 2.5,
          py: 1.3,
          fontSize: "0.938rem",
          fontWeight: 600,
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
      </Button>
    </AuthLayout>
  );
};
